/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {GOOGLE_MAPS_URL} from '../constants';
import {
  EARTH_RADIUS,
  GEOFENCE_BUFFER_FACTORIAL,
  RECTANGULAR_GEOFENCE_INITIAL_BUFFER
} from '../geofences/constants';

export enum NavigationView {
  STREET = 'STREET',
  NAVIGATION = 'Navigation'
};

/**
 * @getMetersInDegree
 *
 * Takes an argument of desired meters and returns the
 * degrees reading for use as lat/long. So if you wanted
 * 10 meters returned as a degree reading you would pass
 * 10 as the argument
 *
 * ```typescript
 *
 *   import {getMetersInDegree} from '@ion/dls/src/maps/helpers';
 *
 *   const degrees = getMetersInDegree(10);
 *
 */
export const getMetersInDegree = (meters) => {
  return 1 / (((2 * Math.PI) / 360) * EARTH_RADIUS) / (1000 / meters);
};

/**
 * @getExponentialBuffer
 *
 * Uses negative exponetiation to calculate the buffer needed
 * (in vertices) for a drawing graphic based on the zoom level provided. Basically, the
 * smaller the zoom level (the farther out) the higher the buffer would be
 * based on a starting buffer at zoom 1 of 700,000.
 *
 * ```typescript
 *
 *   import {getExponentialBuffer} from '@ion/dls/src/maps/helpers';
 *
 *   const buffer = getExponentialBuffer(10); // 2598.46
 *
 */
export const getExponentialBuffer = (zoom: number) => {
  return RECTANGULAR_GEOFENCE_INITIAL_BUFFER * (GEOFENCE_BUFFER_FACTORIAL ** -zoom);
};

/**
 * @navigateToGoogleStreetView
 *
 * Open new tab and navigate to Google street by given latitude and longitude.
 *
 * ```typescript
 *
 *   import {navigateToGoogleStreetView} from '@ion/dls/src/maps/helpers/globeUtils';
 *
 *   navigateToGoogleStreetView({
 *      latitude: 48.857832,
 *      longitude: 2.295226
 *   });
 */
export const navigateToGoogleStreetView = ({latitude, longitude}) => {
  const url = `${GOOGLE_MAPS_URL}/@?api=1&map_action=pano&viewpoint=${latitude},${longitude}`;
  window.open(url, '_blank');
};

/**
 * @navigateToGoogleDirections
 *
 * Open new tab and navigate to Google direction by given latitude and longitude.
 *
 * ```typescript
 *
 *   import {navigateToGoogleDirections} from '@ion/dls/src/maps/helpers';
 *
 *   navigateToGoogleDirections({
 *      latitude: 48.857832,
 *      longitude: 2.295226
 *   });
 */
export const navigateToGoogleDirections = ({latitude, longitude}) => {
  const url = `${GOOGLE_MAPS_URL}/dir/?api=1&destination=${latitude},${longitude}`;
  window.open(url, '_blank');
};
